import { useParticipant } from "../hooks/webrtc/useParticipant";
import { getRankLabel, RankEnum } from "@presidents/game-engine";
import { HandStat } from "@presidents/game-engine/src/game-room";
import type { Player } from "@presidents/game-engine/src/types";
import { Participant } from "livekit-client";
import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { useUser } from "../context/UserContext";
import { MiniCardBack } from "./Card";
import Asshole from "./icons/Asshole";
import { MicrophoneOff } from "./icons/Microphone";
import President from "./icons/President";
import { VideoOff } from "./icons/Video";
import { Rank } from "./PlayerRanks";
import { filterUndefined } from "../utils/filter-undef";
import { useSharedState } from "../hooks/useSharedState";
import { useFaceTracking } from "../hooks/useFaceTracking";
import { FaceMeshOverlay } from "./FaceMeshOverlay";

const Stats = styled.div`
  background-color: hsla(var(--layer3HSL), 0.5);
  backdrop-filter: blur(4px);
  padding: 6px;
  border-radius: 4px;
  font-size: 11px;
  display: flex;
  align-items: center;

  & ${MiniCardBack} {
    transition: transform 0.2s ease-out;
  }

  & ${MiniCardBack}[data-selected] {
    transform: translateY(-4px);
  }
  & ${MiniCardBack} + ${MiniCardBack} {
    position: relative;
    margin-left: -16px;
  }
  &[data-empty] {
    background: none;
    padding: 0;
  }
  /* .right & ${MiniCardBack} + ${MiniCardBack} {
    margin-left: 0px;
    margin-right: -16px;
  }

  .top & {
    bottom: 4px;
  }
  .bottom & {
    top: 4px;
  }
  .right & {
    left: auto;
    right: 4px;
    flex-direction: row-reverse;
  } */
`;

const Element = styled.div<{ isActive?: boolean }>`
  border: 1px solid transparent;
  box-shadow: 0 0 100px rgba(0, 0, 0, 0.5) inset;
  &:before,
  &:after {
    display: block;
    content: "";
    background: transparent;
    border: 4px solid var(--success);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.3s ease-out;
    ${(props) =>
      props.isActive &&
      css`
        opacity: 1;
      `}
  }
  &:before {
    background-color: var(--successMuted);
  }
  ${(props) =>
    props.isActive &&
    css`
      & ${Stats} {
        background-color: var(--successMuted);
      }
    `}
  transition: width 0.2s ease-out, height 0.2s ease-out;
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  overflow: hidden;

  &[data-is-me="true"] {
    color: #fff;
  }
`;

const PlayerName = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  & svg {
    display: inline-block;
    vertical-align: middle;
  }
`;

const PlayerNameSmall = styled.span`
  font-family: var(--altFontFamily);
  font-size: 14px;
  color: #fff;
  text-shadow: 0 0 2px var(--background), 0 0 4px var(--background),
    0 0 6px var(--background), 0 0 8px var(--background);
  white-space: nowrap;
  & svg {
    display: inline-block;
    vertical-align: middle;
    filter: drop-shadow(0 0 2px var(--background)),
      drop-shadow(0 0 4px var(--background)),
      drop-shadow(0 0 6px var(--background), 0 0 8px var(--background));
  }
`;

const VideoPlayer = styled.video<{
  visuallyHidden?: boolean;
  shouldMirror?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 5px;
  /* background: #000; */
  &[data-is-me="false"]:not([data-portrait]) {
    object-fit: none;
    object-fit: cover;
  }

  ${(props) =>
    props.shouldMirror &&
    css`
      transform: scale(-1, 1);
    `}
  ${(props) =>
    props.visuallyHidden &&
    css`
      width: 1px;
      height: 1px;
      opacity: 0.1;
    `}
`;

const VideoOverlays = styled.div`
  padding: 5px;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: var(--overlay-align, flex-start);
  justify-content: var(--overlay-justify, flex-end);
`;

type Props = {
  player: Player;
  rank?: RankEnum | null;
  isPresident?: boolean;
  isAsshole?: boolean;
  position: number;
  total: number;
  isActive?: boolean;
  selected: number[];
  stats?: HandStat | null;
  participant?: Participant | null;
};
const PlayerSection: React.FC<Props> = ({
  rank,
  player,
  position,
  isActive,
  selected,
  stats,
  participant
}) => {
  const isMe = useUser()?.uid === player.id;
  const {
    isLocal,
    isAudioMuted: isMuted,
    cameraPublication,
    microphonePublication
  } = useParticipant(participant);

  const cameraFacingMode = cameraPublication?.track?.mediaStreamTrack?.getSettings()
    .facingMode;
  const shouldMirror = isMe && cameraFacingMode === "user";
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const videoTrack = cameraPublication?.track;
  const [userSeed] = useSharedState(`userSeed:${player.id}`, "");
  const { faceTrackingEnabled } = useFaceTracking();

  useEffect(() => {
    let mounted = true;

    const attachTrack = async () => {
      if (!mounted || !videoElementRef.current || !videoTrack) return;

      try {
        if (videoElementRef.current.srcObject) {
          const tracks = (videoElementRef.current
            .srcObject as MediaStream).getTracks();
          tracks.forEach((track) => track.stop());
          videoElementRef.current.srcObject = null;
        }

        await videoTrack.attach(videoElementRef.current);
      } catch (err) {
        // Silent error handling - we don't want to break the UI
      }
    };

    attachTrack();

    return () => {
      mounted = false;
      if (videoElementRef.current && videoTrack) {
        try {
          videoTrack.detach(videoElementRef.current);
          if (videoElementRef.current.srcObject) {
            const tracks = (videoElementRef.current
              .srcObject as MediaStream).getTracks();
            tracks.forEach((track) => track.stop());
            videoElementRef.current.srcObject = null;
          }
        } catch (err) {
          // Silent error handling
        }
      }
    };
  }, [videoTrack, player.id]);

  const audioEl = useRef<HTMLAudioElement>();
  const track = microphonePublication?.track;

  useEffect(() => {
    if (isLocal) return;

    let mounted = true;

    const attachAudio = async () => {
      if (!mounted || !track) return;

      try {
        audioEl.current = track.attach();
        if (track.sid) {
          audioEl.current.setAttribute("data-audio-track-id", track.sid);
        }
      } catch (err) {
        // Silent error handling
      }
    };

    attachAudio();

    return () => {
      mounted = false;
      if (track) {
        try {
          track.detach().forEach((el) => el.remove());
        } catch (err) {
          // Silent error handling
        }
      }
    };
  }, [track, isLocal]);

  const hasVideo = !!participant?.isCameraEnabled;
  const isPresident = rank === RankEnum.president;
  const isAsshole = rank === RankEnum.asshole;

  return (
    <Element
      data-is-me={isLocal}
      data-id={player.id}
      data-name={player.name}
      isActive={isActive}
    >
      {!hasVideo && (
        <PlayerName>
          {isPresident && <President />}
          {isAsshole && <Asshole />} {player.name} {isMe && "(You)"}{" "}
          {!hasVideo && <VideoOff />} {isMuted && <MicrophoneOff />}
        </PlayerName>
      )}
      <VideoPlayer
        ref={videoElementRef}
        id={`v-${player.id}`}
        data-id={player.id}
        data-is-me={isMe}
        autoPlay
        playsInline
        visuallyHidden={!hasVideo}
        shouldMirror={shouldMirror}
        muted={isMuted || isMe}
        onPause={(e) => {
          e.preventDefault();
        }}
      />
      {isLocal && hasVideo && (
        <FaceMeshOverlay
          videoRef={videoElementRef}
          enabled={faceTrackingEnabled}
          shouldMirror={shouldMirror}
        />
      )}
      <VideoOverlays>
        {isMe ? (
          <Stats data-empty>
            <PlayerNameSmall>
              {rank && <Rank data-rank={rank}>{getRankLabel(rank)}</Rank>}
            </PlayerNameSmall>
          </Stats>
        ) : (
          <>
            {hasVideo && (
              <PlayerNameSmall>
                {player.name}{" "}
                {rank && <Rank data-rank={rank}>{getRankLabel(rank)}</Rank>}{" "}
                {isMuted && <MicrophoneOff />}
              </PlayerNameSmall>
            )}
            <Stats id={"other-hand-" + player.id}>
              {stats?.numCards && stats.numCards > 0
                ? Array.from({ length: stats.numCards }).map((_, i) => (
                    <MiniCardBack
                      key={i}
                      data-owner={player.id}
                      data-selected={
                        selected?.includes(i) ? "selected" : undefined
                      }
                    />
                  ))
                : stats?.isOut
                ? "OUT!"
                : null}
            </Stats>
          </>
        )}
      </VideoOverlays>
    </Element>
  );
};

export default PlayerSection;
